import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import { useSignInForm } from "../hooks/useSignInForm";
import OTPCodeInput from "./UI/DesignStandards/Forms/OTPCodeInput";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";

export default function SignInForm() {
  const {
    formData,
    errors,
    loading,
    captchaToken,
    success,
    signInMethod,
    showOTPEmailInput,
    otpCodeSent,
    otpCode,
    sendingOTP,
    otpError,
    handleChange,
    handleSubmit,
    handleCaptchaError,
    handleCaptchaExpire,
    handleCaptchaVerify,
    handleStartOTPFlow,
    handleSendOTP,
    handleVerifyOTP,
    handleResendOTP,
    handleBackToPassword,
  } = useSignInForm();

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading && captchaToken) {
      handleSubmit(e);
    }
  };

  // Focus management
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (errors.email && emailInputRef.current) {
      emailInputRef.current.focus();
    } else if (errors.password && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [errors.email, errors.password]);

  return (
    <section className="min-h-screen bg-brand-cream flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="card-board overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left column - Form */}
            <div className="w-full lg:w-1/2 p-4 sm:p-8 lg:p-12 min-w-0">
              <div className="text-center mb-8">
                <h1 className="heading-display text-2xl mb-2">
                  Welcome Back
                </h1>
                <p className="text-brand-muted">
                  Sign in to your account to continue
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                aria-label="Sign in form"
              >
                {/* Email input - show for password sign-in or OTP flow */}
                {(signInMethod === "password" || showOTPEmailInput) && (
                  <div className="relative">
                    <label
                      htmlFor="email"
                      className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                    >
                      Email address
                    </label>
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope
                        className="h-5 w-5 text-brand-muted"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                      className={`pl-10 w-full p-3 border ${
                        errors.email ? "border-red-300" : "border-brand-charcoal/20"
                      } rounded-lg text-brand-charcoal placeholder-brand-mist focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent transition duration-200 ${
                        loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      required
                      autoComplete="username"
                      autoFocus={showOTPEmailInput}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      aria-invalid={errors.email ? "true" : "false"}
                      ref={emailInputRef}
                      onKeyPress={handleKeyPress}
                    />
                    {errors.email && (
                      <p
                        id="email-error"
                        className="mt-1 text-sm text-red-600"
                        role="alert"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>
                )}

                {/* OTP Email Input Section - show when user clicks "Sign in with verification code" */}
                {showOTPEmailInput && !otpCodeSent && (
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={handleSendOTP}
                      disabled={loading || sendingOTP || !formData.email}
                      className="w-full py-3 px-4 rounded-lg font-display font-bold tracking-wide transition duration-200 bg-brand-cream text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-gold-ink disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingOTP ? "Sending code..." : "Send Verification Code"}
                    </button>
                    {otpError && (
                      <p className="text-sm text-red-600 text-center">{otpError}</p>
                    )}
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleBackToPassword}
                        disabled={loading || sendingOTP}
                        className="text-sm text-brand-gold-ink hover:text-brand-charcoal transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed underline"
                      >
                        Sign in with password instead
                      </button>
                    </div>
                  </div>
                )}

                {/* Password input - only show if not using OTP */}
                {signInMethod === "password" && !otpCodeSent && (
                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                    >
                      Password
                    </label>
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock
                        className="h-5 w-5 text-brand-muted"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      className={`pl-10 w-full p-3 border ${
                        errors.password ? "border-red-300" : "border-brand-charcoal/20"
                      } rounded-lg text-brand-charcoal placeholder-brand-mist focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent transition duration-200 ${
                        loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      required
                      autoComplete="current-password"
                      aria-describedby={
                        errors.password ? "password-error" : undefined
                      }
                      aria-invalid={errors.password ? "true" : "false"}
                      ref={passwordInputRef}
                      onKeyPress={handleKeyPress}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-muted hover:text-brand-charcoal focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-5 w-5" aria-hidden="true" />
                      ) : (
                        <FaEye className="h-5 w-5" aria-hidden="true" />
                      )}
                    </button>
                    {errors.password && (
                      <p
                        id="password-error"
                        className="mt-1 text-sm text-red-600"
                        role="alert"
                      >
                        {errors.password}
                      </p>
                    )}
                  </div>
                )}

                {/* Option to request OTP code - always visible for password sign-in */}
                {signInMethod === "password" && !showOTPEmailInput && (
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={handleStartOTPFlow}
                      disabled={loading}
                      className="text-sm text-brand-gold-ink hover:text-brand-charcoal transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed underline"
                    >
                      Sign in with verification code instead
                    </button>
                  </div>
                )}

                {/* Remember Me checkbox */}
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 text-brand-charcoal focus:ring-brand-gold-ink border-brand-charcoal/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-brand-charcoal disabled:opacity-50"
                  >
                    Remember me
                  </label>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div
                    className="p-4 bg-red-50 border border-red-200 rounded-md"
                    role="alert"
                    aria-live="polite"
                  >
                    <p className="text-sm text-red-600">{errors.submit}</p>
                  </div>
                )}

                {/* Success message */}
                {success && (
                  <div
                    className="p-4 card-inset"
                    role="alert"
                    aria-live="polite"
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-5 h-5 text-brand-charcoal mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-brand-charcoal">
                        Sign in successful! Redirecting...
                      </p>
                    </div>
                  </div>
                )}

                {/* CAPTCHA */}
                <div className="flex justify-center max-w-full overflow-x-auto">
                  <div role="group" aria-label="Security verification">
                    <Turnstile
                      siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                      onSuccess={handleCaptchaVerify}
                      onError={handleCaptchaError}
                      onExpire={handleCaptchaExpire}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Submit button - only show for password sign-in */}
                {signInMethod === "password" && !otpCodeSent && (
                  <>
                    <button
                      type="submit"
                      disabled={loading || !captchaToken}
                      className={`w-full py-3 px-4 rounded-lg font-display font-bold tracking-wide transition duration-200 ${
                        loading || !captchaToken
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal font-display font-bold"
                      }`}
                      aria-describedby={
                        !captchaToken ? "captcha-required" : undefined
                      }
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-charcoal"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span aria-live="polite">Signing in...</span>
                        </div>
                      ) : (
                        "Sign in"
                      )}
                    </button>

                    {!captchaToken && (
                      <p
                        id="captcha-required"
                        className="text-sm text-brand-muted text-center"
                      >
                        Please complete the security verification to continue
                      </p>
                    )}
                  </>
                )}

              </form>

              {/* OTP Code Input - above the account links, outside the form to avoid nested forms */}
              {otpCodeSent && signInMethod === "otp" && (
                <div className="mt-6">
                  <OTPCodeInput
                    email={formData.email}
                    onVerify={handleVerifyOTP}
                    onCodeChange={handleVerifyOTP}
                    onBack={handleBackToPassword}
                    onResend={handleResendOTP}
                    loading={loading || sendingOTP}
                    error={otpError}
                    verifying={loading}
                    hideVerifyButton={true}
                  />
                </div>
              )}

              <div className="mt-6 flex flex-col gap-2">
                <Link
                  to="/forgot-password"
                  className={`inline-flex items-center min-h-11 text-sm text-brand-gold-ink hover:text-brand-charcoal transition duration-200 ${
                    loading ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  Forgot your password?
                </Link>
                <Link
                  to="/signup"
                  className={`inline-flex items-center min-h-11 text-sm text-brand-gold-ink hover:text-brand-charcoal transition duration-200 ${
                    loading ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  Don't have an account? Sign up
                </Link>
              </div>
            </div>

            {/* Right column - Info */}
            <div className="hidden lg:block lg:w-1/2 bg-brand-charcoal text-brand-cream border-l border-brand-line">
              <div className="h-full flex flex-col justify-center items-center p-12 text-center">
                <div className="max-w-md">
                  <h3 className="font-display font-bold text-2xl mb-6 text-brand-cream tracking-tight">
                    Welcome Back to Your Tournament Hub
                  </h3>
                  <p className="text-brand-mist mb-8">
                    Access your tournaments, manage squads, and track results
                    all in one place. We're glad to have you back!
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-brand-line flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-brand-cream"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-brand-cream">
                        Quick access to your tournaments
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-brand-line flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-brand-cream"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-brand-cream">
                        Manage your squads and scores
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-brand-line flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-brand-cream"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-brand-cream">
                        Track your tournament progress
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
